import { Schema } from "effect";
import { Finding } from "./finding.ts";

// "Ready to enrich" (CON-1314): the document converts cleanly AND carries no critical finding.
//
// Critical means "would make the pipeline's push fail" on the AUDIT-SEVERITY portion of the gate,
// which is not a judgement call: `tools/push_local_exercises_to_databricks.py` refuses to push on
// any audit check with `severity="error"` and prints warnings as "non-blocking". So `fatal` is
// critical, `warning` is not — and if that gate ever starts blocking on warnings, this mapping is
// the one place that changes.
//
// The count folds THREE registries: the conversion's own findings, the markdown-dialect lint, and
// the authored audit. Only the last is what the push gate's severity rule is derived from, so the
// inclusion of the other two needs its own justification: a document that breaches the dialect
// cannot be parsed into the model the conversion reads, and one that does not convert has no
// exercise to push — both are blockers by a shorter argument than the audit's, not by the same one.
//
// SCOPE, said plainly so the card is not read as more than it is. READY here means "nothing in this
// DOCUMENT blocks enrichment". It is not a promise the push will succeed, because that same
// pre-push gate also refuses for reasons a curriculum document cannot express: a missing or
// rejected `evaluation_verdict`, more than one `curriculum_id` for the run, a `curriculum_id`
// absent from `curriculum_distributed.json`, a `level_id` that disagrees with the locator. Those
// are properties of a generated RUN, not of the markdown under review, and the reviewer looking at
// this card has no way to act on them. Nothing enforces the severity mapping either: an authored
// rule severitied `warning` here whose downstream counterpart is an `error` check would read READY
// and still be refused. `.claude/skills/push-audit-parity` is the mechanism for keeping the two
// registries aligned; it applies whenever either side gains a check.

// The seven fields every conversion-finding view shares (Decision 4, CON-1334): extracted so the
// web wire schema here and MCP's `ConversionFindingView` (`run-checks-result.ts`) spread the same
// field list rather than hand-copying it in two places that must stay in lockstep.
export const ConversionFindingBaseFields = {
  ruleId: Schema.String,
  severity: Schema.Literal("fatal", "warning"),
  message: Schema.String,
  docs: Schema.String,
  headingPath: Schema.Array(Schema.String),
  // The ANCHOR, and the reason this is worth shipping: CON-1314 asks for a conversion failure
  // "anchored at the offending span, not a generic error", and a count cannot be anchored. All
  // three are needed together — `diagnostics-client.ts`'s `toCmDiagnosticFromFinding` computes a
  // CodeMirror mark from exactly `line` + `column` + `span.length`, so dropping `column`/`span`
  // (an earlier revision did) leaves the finding renderable only as a bare line label, with no
  // squiggle, while a structurally identical lint finding one card above gets one.
  line: Schema.Number,
  column: Schema.Number,
  span: Schema.String,
};

// One conversion finding, on the wire. Deliberately NOT `Finding`: that type closes `ruleId` over
// `ALL_FINDING_IDS` and `specSection` over `Citation`, and the conversion contributes rule ids the
// lint registry does not know (`CONVERSION_FAILED`, `EXERCISE_MISSING_GENERATED_FIELD`, …) from a
// document neither citation set covers. Widening those unions to admit them would weaken the
// exhaustiveness the lint tables rely on, so the conversion travels as its own array — the same
// shape CON-744 chose for `lint` and `audit` rather than merging them and re-deriving the split.
export const ConversionFindingSchema = Schema.Struct({
  ...ConversionFindingBaseFields,
  // CON-1334, Decision 4 (widened for `CONVERSION_MISSING_AUTHORED_CONTENT`): the rail's
  // agent-facing remediation text, populated only for the conversion rule ids
  // `curriculum-check`'s `instructForConversion` covers and absent for every other conversion
  // rule id — mirrors `RailFinding.instructText` below, which is likewise always absent for a
  // family with nothing to say.
  instructText: Schema.optional(Schema.String),
});
export type ConversionFindingWire = typeof ConversionFindingSchema.Type;

// U8 (CON-1315, Decision 11): a conversion finding, reshaped for the SAME diagnostics rail that
// already renders `lint`/`audit` (`finding-queue.tsx`'s `groupFindingsByRule`), instead of only the
// "Ready to enrich" card. Deliberately NOT `Finding` — widening `Finding.ruleId` (closed over
// `ALL_FINDING_IDS`) to admit `EXERCISE_MISSING_GENERATED_FIELD` and friends would weaken the
// exhaustiveness check every OTHER `Finding` consumer relies on, for ids that were never lint/audit
// registry members. `subKind: "conversion"` is the tag `groupFindingsByRule` groups on, so a
// conversion finding never collapses into a rule finding's group even if the two ever shared a
// `ruleId` string, and the rail can render it visibly apart from a rule-sourced finding.
export interface ConversionRailFinding {
  readonly subKind: "conversion";
  readonly ruleId: string;
  readonly severity: "fatal" | "warning";
  readonly message: string;
  readonly headingPath: readonly string[];
  readonly line: number;
  readonly column: number;
  // CON-1334, Decision 4: mirrors `ConversionFindingSchema.instructText` — populated only for the
  // conversion rule ids `instructForConversion` covers, absent for every other conversion rule id.
  readonly instructText?: string;
}

export const toConversionRailFinding = (finding: ConversionFindingWire): ConversionRailFinding => ({
  subKind: "conversion",
  ruleId: finding.ruleId,
  severity: finding.severity,
  message: finding.message,
  headingPath: finding.headingPath,
  line: finding.line,
  column: finding.column,
  ...(finding.instructText === undefined ? {} : { instructText: finding.instructText }),
});

export const toConversionRailFindings = (
  findings: readonly ConversionFindingWire[],
): readonly ConversionRailFinding[] => findings.map(toConversionRailFinding);

export const isCriticalFinding = (finding: { readonly severity: "fatal" | "warning" }): boolean =>
  finding.severity === "fatal";

// U11 (CON-1315, Decisions 12 & 14): a `Finding` widened with the rail's own agent-facing remediation
// text, when the finding's rule carries an `instruct` (`curriculum-check`'s `instructFor`). This is the
// U11 plan's deliberate, narrow reversal of the boundary this file's own history assumed permanent —
// "remediation text is agent-facing only" (see `packages/mcp/src/ops/run-checks-result.ts`'s
// `AuditFindingView` and `curriculum-check`'s `instructFor`, both updated alongside this): the web
// rail's `[Fix]` affordance for an `instruct`-only finding (no mechanical fix, U11's plan) needs that
// same text to show the reviewer what to do by hand, so it now reaches the rail too — through THIS
// widened shape, never through `Finding` itself. `Finding` stays exactly as every other consumer
// (the audit log, the MCP `lint`/`audit` families, every match arm across both apps) already assumes
// it: closed over `ALL_FINDING_IDS`/`Citation`, carrying no remediation prose. Mirrors
// `ConversionRailFinding` above — a widened, rail-specific shape assembled at the one call site that
// needs it (`diagnostics.ts`'s `computeDiagnostics`), not a change to the shared wire type.
export const RailFinding = Schema.Struct({
  ...Finding.fields,
  instructText: Schema.optional(Schema.String),
});
export type RailFinding = typeof RailFinding.Type;

export interface EnrichmentReadinessInput {
  // Did every authored exercise that was ATTEMPTED assemble? Structural only — an exercise the
  // conversion skipped because a generated field is not written yet leaves this true.
  readonly convertsCleanly: boolean;
  // How many of the document's authored exercises produced enrichment JSON, and how many there are.
  // Separate from `convertsCleanly` because they answer different questions and the difference is
  // the whole gate: a document whose D3 fields are unwritten converts "cleanly" and yields a third
  // of its exercises.
  // Reported on the card, not gated on — see `enrichmentReadiness`.
  readonly exercisesConverted: number;
  readonly exercisesTotal: number;
  // Every finding on the document: the conversion's own, plus the authored audit's.
  readonly findings: readonly { readonly severity: "fatal" | "warning" }[];
}

// Wire form: the server computes readiness, because the client cannot run the conversion half.
export const EnrichmentReadinessSchema = Schema.Struct({
  ready: Schema.Boolean,
  convertsCleanly: Schema.Boolean,
  criticalCount: Schema.Number,
  warningCount: Schema.Number,
  exercisesConverted: Schema.Number,
  exercisesTotal: Schema.Number,
  blockedBy: Schema.Array(Schema.String),
});

export interface EnrichmentReadiness {
  readonly ready: boolean;
  readonly convertsCleanly: boolean;
  readonly criticalCount: number;
  readonly warningCount: number;
  readonly exercisesConverted: number;
  readonly exercisesTotal: number;
  // Why it is not ready, in the order a reader should act on them. Empty when ready.
  readonly blockedBy: readonly string[];
}

// What readiness is before the server has computed any: NOT ready, with the reason said out loud.
// One exported value rather than three copies of the same object literal — it is the wire default
// (`DiagnosticsPayload`), the client's pre-connection state, and what a surface with no collab
// connection shows, and a reworded blocker must not mean three different things in one UI.
export const READINESS_NOT_COMPUTED: EnrichmentReadiness = {
  ready: false,
  convertsCleanly: false,
  criticalCount: 0,
  warningCount: 0,
  exercisesConverted: 0,
  exercisesTotal: 0,
  blockedBy: ["readiness has not been computed for this document yet"],
};

export const enrichmentReadiness = (input: EnrichmentReadinessInput): EnrichmentReadiness => {
  const criticalCount = input.findings.filter(isCriticalFinding).length;
  const warningCount = input.findings.length - criticalCount;
  const blockedBy: string[] = [];
  // Conversion first: a document that does not convert cannot be enriched at all, whereas a
  // critical finding is a content problem the author can act on.
  if (!input.convertsCleanly)
    blockedBy.push("the document does not convert cleanly to enrichment JSON");
  if (criticalCount > 0) {
    blockedBy.push(
      `${criticalCount} critical finding${criticalCount === 1 ? "" : "s"} would make the push fail`,
    );
  }
  // The counts are REPORTED, not gated on. Running this over a corpus document showed the card
  // saying "Ready" with 6 of 19 exercises built, which reads as a false green — but gating on it
  // would be worse: `image_description` is D4, permanently supplied by the pipeline and never by
  // the document, so every cloze exercise is un-buildable from the document alone and no document
  // with one would ever be ready. "Ready to enrich" means the ENRICHMENT step can run, and
  // enrichment is what supplies those fields. The card shows the ratio so a reviewer can see how
  // much of the document stands on its own; it climbs as D3's expander fills the rest (CON-1334).
  return {
    ready: blockedBy.length === 0,
    convertsCleanly: input.convertsCleanly,
    criticalCount,
    warningCount,
    exercisesConverted: input.exercisesConverted,
    exercisesTotal: input.exercisesTotal,
    blockedBy,
  };
};
